import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { apiService } from 'services/base.service';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { ProcurementParamsType, ProcurementType } from 'models/procurement.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { debounce, handleApiError } from 'utils/functions';
import RequestListFilters from 'components/base/request-list-filters';
import ExecutiveProcurementCard from 'components/base/procurement-card/executiveProcurementCard';
import useDashboardContext from 'contexts/dashboard.context';

export type DateRangeType = {
  startDate: Date;
  endDate: Date;
};

type RequestStatusFilter = ENUM_REQUEST_STATUS;

type ExecutiveProcurementFilters = {
  search: string;
  status: RequestStatusFilter[];
  dateRange?: DateRangeType;
  current_page: number;
};

const ExecutiveProcurement = () => {
  const [isFetchingProcurements, setIsFetchingProcurements] = useState(false);
  const [procurements, setProcurements] = useState<ProcurementType[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState<ExecutiveProcurementFilters>({
    search: '',
    status: [],
    dateRange: undefined,
    current_page: 1
  });
  const { activeStore } = useDashboardContext();

  const fetchProcurements = async (filters: {
    searchString: string;
    status: RequestStatusFilter[];
    current_page?: number;
    dateRangeParam?: DateRangeType;
  }) => {
    if (!activeStore?.id) return;
    try {
      setIsFetchingProcurements(true);

      const params: ProcurementParamsType = {
        store_id: activeStore?.id,
        code: filters.searchString,
        status: filters.status,
        page: filters.current_page || 1
      };
      const { dateRangeParam } = filters;
      if (dateRangeParam?.startDate) {
        params.start_date = dateRangeParam.startDate.toISOString();
      }
      if (dateRangeParam?.endDate) {
        params.end_date = dateRangeParam.endDate.toISOString();
      }

      const response = await apiService.get('procurement', {
        version: 'v2',
        params
      });
      if (response) {
        setProcurements(response.data.data);
        setLastPage(Math.ceil(response.data.pagination.total / response.data.pagination.page_size));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingProcurements(false);
    }
  };

  const debounceFetchProcurements = useCallback(debounce(fetchProcurements, 1000), []);

  useEffect(() => {
    setIsFetchingProcurements(true);
    setProcurements([]);
    debounceFetchProcurements({
      searchString: filters.search,
      status: filters.status,
      current_page: filters.current_page,
      dateRangeParam: filters.dateRange
    });
  }, [filters, activeStore?.id]);

  return (
    <div>
      <PageHeaderText
        title='Executive Procurement'
        description='Manage procurement requests from main store'
      />
      <RequestListFilters
        statusMode='multi'
        statusOptions={[
          { text: ENUM_REQUEST_STATUS.CANCELLED, value: ENUM_REQUEST_STATUS.CANCELLED },
          { text: ENUM_REQUEST_STATUS.APPROVED, value: ENUM_REQUEST_STATUS.APPROVED },
          { text: ENUM_REQUEST_STATUS.REJECTED, value: ENUM_REQUEST_STATUS.REJECTED },
          { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },
          { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED },
          { text: ENUM_REQUEST_STATUS.COMPLETED, value: ENUM_REQUEST_STATUS.COMPLETED }
        ]}
        filters={filters}
        onFilterChange={setFilters}
        pageCount={lastPage}
      />
      {isFetchingProcurements ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : procurements.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>Main store is yet to make any procurement</p>
        </div>
      ) : (
        <div className='mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7'>
          {procurements.map((p, i) => (
            <ExecutiveProcurementCard key={i} procurement={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExecutiveProcurement;
